import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Calendar,
  DollarSign,
  Clock,
  FileText,
  CheckCircle2,
  AlertCircle,
  Info,
} from "lucide-react";
import { CompanyDetails as CompanyDetailsType } from "@/types/company";

// Mock data - in production, this would come from the API
const mockCompanyData: CompanyDetailsType = {
  id: "comp-001",
  code: "ACME",
  name: "Acme Corporation Ltd",
  legalName: "Acme Corporation Limited",
  registrationNumber: "12345678",
  vatNumber: "GB123456789",
  taxNumber: "123/AB45678",
  address: {
    line1: "123 Business Park",
    line2: "Innovation Drive",
    city: "London",
    county: "Greater London",
    postcode: "SW1A 1AA",
    country: "United Kingdom",
  },
  contact: {
    phone: "+44 20 1234 5678",
    email: "info@acmecorp.co.uk",
    website: "www.acmecorp.co.uk",
  },
  settings: {
    currency: "GBP",
    currencySymbol: "£",
    timezone: "Europe/London",
    dateFormat: "DD/MM/YYYY",
    fiscalYearStart: "04-01", // April 1st
    fiscalYearEnd: "03-31", // March 31st
  },
  integration: {
    erpSystem: "Access Financials",
    erpVersion: "2024.1",
    lastSyncDate: "2024-01-15T14:30:00Z",
    syncStatus: "connected",
  },
  metadata: {
    createdDate: "2023-01-15T10:00:00Z",
    dataSource: "Access Financials",
    lastModifiedDate: "2024-01-15T14:30:00Z",
  },
};

export function CompanyDetailsPage() {
  const company = mockCompanyData;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSyncStatusBadge = (status: CompanyDetailsType["integration"]["syncStatus"]) => {
    switch (status) {
      case "connected":
        return (
          <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Connected
          </Badge>
        );
      case "syncing":
        return (
          <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
            <Clock className="h-3 w-3 mr-1" />
            Syncing
          </Badge>
        );
      case "error":
        return (
          <Badge className="bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
            <AlertCircle className="h-3 w-3 mr-1" />
            Error
          </Badge>
        );
      case "disconnected":
        return (
          <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300">
            <AlertCircle className="h-3 w-3 mr-1" />
            Disconnected
          </Badge>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight">Company Details</h1>
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            🟡 Demo Mode
          </Badge>
        </div>
        <p className="text-muted-foreground mt-2">
          Company information synced from {company.metadata.dataSource}
        </p>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Read-Only Information
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Company details are automatically synced from {company.integration.erpSystem} and
                cannot be edited here. To update company information, please make changes in your
                ERP system and they will be reflected here after the next sync.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Information
            </CardTitle>
            <CardDescription>Basic company details and registration</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Company Code</label>
                <p className="text-base font-mono mt-1">{company.code}</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground">Trading Name</label>
                <p className="text-base font-semibold mt-1">{company.name}</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground">Legal Name</label>
                <p className="text-base mt-1">{company.legalName}</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Company Registration Number
                </label>
                <p className="text-base font-mono mt-1">{company.registrationNumber || "—"}</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground">VAT Number</label>
                <p className="text-base font-mono mt-1">{company.vatNumber || "—"}</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground">Tax Number</label>
                <p className="text-base font-mono mt-1">{company.taxNumber || "—"}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Address & Contact
            </CardTitle>
            <CardDescription>Company location and contact details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2 mb-2">
                <MapPin className="h-4 w-4" />
                Registered Address
              </label>
              <div className="text-base space-y-1 pl-6">
                <p>{company.address.line1}</p>
                {company.address.line2 && <p>{company.address.line2}</p>}
                <p>{company.address.city}</p>
                {company.address.county && <p>{company.address.county}</p>}
                <p>{company.address.postcode}</p>
                <p className="font-medium">{company.address.country}</p>
              </div>
            </div>
            <Separator />
            <div className="space-y-3">
              {company.contact.phone && (
                <>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone
                    </label>
                    <p className="text-base mt-1 pl-6">{company.contact.phone}</p>
                  </div>
                  <Separator />
                </>
              )}
              {company.contact.email && (
                <>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email
                    </label>
                    <p className="text-base mt-1 pl-6">{company.contact.email}</p>
                  </div>
                  <Separator />
                </>
              )}
              {company.contact.website && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Website
                  </label>
                  <p className="text-base mt-1 pl-6">{company.contact.website}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Financial Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Financial Settings
            </CardTitle>
            <CardDescription>Currency and fiscal year configuration</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Base Currency</label>
                <p className="text-base mt-1">
                  {company.settings.currency} ({company.settings.currencySymbol})
                </p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground">Timezone</label>
                <p className="text-base mt-1">{company.settings.timezone}</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground">Date Format</label>
                <p className="text-base mt-1">{company.settings.dateFormat}</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Fiscal Year
                </label>
                <p className="text-base mt-1 pl-6">
                  {company.settings.fiscalYearStart} to {company.settings.fiscalYearEnd}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Integration Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Integration Status
            </CardTitle>
            <CardDescription>ERP system connection and sync information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground">ERP System</label>
                <p className="text-base font-semibold mt-1">{company.integration.erpSystem}</p>
              </div>
              <Separator />
              {company.integration.erpVersion && (
                <>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Version</label>
                    <p className="text-base mt-1">{company.integration.erpVersion}</p>
                  </div>
                  <Separator />
                </>
              )}
              <div>
                <label className="text-sm font-medium text-muted-foreground">Sync Status</label>
                <div className="mt-1">{getSyncStatusBadge(company.integration.syncStatus)}</div>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground">Last Sync</label>
                <p className="text-base mt-1">{formatDate(company.integration.lastSyncDate)}</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground">Data Source</label>
                <p className="text-base mt-1">{company.metadata.dataSource}</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Last Modified
                </label>
                <p className="text-base mt-1">{formatDate(company.metadata.lastModifiedDate)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
